import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useSearchParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material'
import { useAuth } from '../../context/AuthContext'
import { useTenant } from '../../context/TenantContext'
import api from '../../lib/api'
import { joinSchema } from '../../lib/schemas'
import { STANDARD_FORM_OPTIONS, isSubmitDisabled } from '../../lib/formDefaults'
import { Logo, PoweredByLogo } from '../common/Logo'
import { roleHomePath } from '../../lib/roleHomePath'

/**
 * Join page: accept invite via token in URL, set password, then redirect to dashboard.
 * Route: /join?token=...
 */

export function JoinPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { fetchUserProfile } = useAuth()
  const { tenant } = useTenant()
  const token = searchParams.get('token')

  const [step, setStep] = useState(() => (token ? 'loading' : 'error')) // loading | form | error
  const [inviteUser, setInviteUser] = useState(null)
  const [error, setError] = useState(() => (token ? '' : 'Missing invite link. Please use the link from your invite email.'))
  const supportEmail = import.meta.env.VITE_SUPPORT_EMAIL || 'support@thesocialbox.co'

  const {
    register,
    handleSubmit,
    reset,
    setError: setFormError,
    formState: { errors, isSubmitting, isValid },
  } = useForm({
    ...STANDARD_FORM_OPTIONS,
    resolver: zodResolver(joinSchema),
    defaultValues: { password: '', confirmPassword: '', firstName: '', lastName: '' },
  })

  useEffect(() => {
    if (!token) return
    let cancelled = false
    ;(async () => {
      try {
        const data = await api.auth.joinVerify(token)
        if (!cancelled) {
          setInviteUser(data.user)
          reset({
            password: '',
            confirmPassword: '',
            firstName: data.user?.first_name || '',
            lastName: data.user?.last_name || '',
          })
          setStep('form')
        }
      } catch (e) {
        if (!cancelled) {
          setError(e.message || 'Invalid or expired invite link.')
          setStep('error')
        }
      }
    })()
    return () => { cancelled = true }
  }, [token, reset])

  const onSubmit = async (data) => {
    try {
      await api.auth.joinComplete(token, data.password, {
        firstName: data.firstName,
        lastName: data.lastName,
      })
      await fetchUserProfile()
      navigate(roleHomePath(inviteUser, { influencerAfterJoin: true }), { replace: true })
    } catch (e) {
      setFormError('root', { message: e.message || 'Something went wrong. Please try again.' })
    }
  }

  if (step === 'loading') {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (step === 'error') {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
        <Paper sx={{ p: 4, maxWidth: 400 }}>
          <Typography variant="h6" color="error" gutterBottom>Invalid invite link</Typography>
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Ask your account manager to resend the invite, or contact {supportEmail}.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" onClick={() => navigate('/login')}>Go to login</Button>
            <Button component="a" href={`mailto:${supportEmail}?subject=Invite%20link%20help`} variant="text">
              Contact support
            </Button>
          </Box>
        </Paper>
      </Box>
    )
  }

  const displayName = inviteUser?.full_name || [inviteUser?.first_name, inviteUser?.last_name].filter(Boolean).join(' ') || inviteUser?.email

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
      <Container maxWidth="xs">
        <Paper elevation={0} sx={{ p: 4, borderRadius: 3, border: (t) => `1px solid ${t.palette.divider}` }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
            {tenant?.logoUrl ? (
              <Box component="img" src={tenant.logoUrl} alt="" sx={{ height: 52, mb: 1, objectFit: 'contain' }} />
            ) : (
              <Logo variant="lavender" height={52} sx={{ mb: 1 }} />
            )}
            {tenant?.displayName && (
              <Typography variant="subtitle1" color="text.secondary">{tenant.displayName}</Typography>
            )}
          </Box>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            {inviteUser?.role === 'influencer' ? 'Activate your creator account' : 'Set your password'}
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Welcome{displayName && displayName !== inviteUser?.email ? `, ${displayName}` : ''}. Add your name and
            choose a password to get started.
          </Typography>
          {errors.root && <Alert severity="error" sx={{ mb: 2 }}>{errors.root.message}</Alert>}
          <Box component="form" onSubmit={handleSubmit(onSubmit)}>
            <TextField
              fullWidth
              label="First name"
              required
              autoComplete="given-name"
              error={!!errors.firstName}
              helperText={errors.firstName?.message}
              sx={{ mb: 2 }}
              {...register('firstName')}
            />
            <TextField
              fullWidth
              label="Last name"
              autoComplete="family-name"
              error={!!errors.lastName}
              helperText={errors.lastName?.message}
              sx={{ mb: 2 }}
              {...register('lastName')}
            />
            <TextField
              fullWidth
              label="Password"
              type="password"
              required
              autoComplete="new-password"
              error={!!errors.password}
              helperText={errors.password?.message}
              sx={{ mb: 2 }}
              slotProps={{ htmlInput: { minLength: 6 } }}
              {...register('password')}
            />
            <TextField
              fullWidth
              label="Confirm password"
              type="password"
              required
              autoComplete="new-password"
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword?.message}
              sx={{ mb: 3 }}
              {...register('confirmPassword')}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={isSubmitDisabled({ isValid, isSubmitting })}
            >
              {isSubmitting ? 'Activating...' : 'Activate account'}
            </Button>
            {tenant && <PoweredByLogo />}
          </Box>
        </Paper>
      </Container>
    </Box>
  )
}
